import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MapPin, Clock, Users, Home as HomeIcon, Check, X, Plus, ChevronRight,
} from 'lucide-react';
import { addWeeks, format } from 'date-fns';
import { useAuth } from '../context/AuthContext';
import { useEvents } from '../hooks/useEvents';
import { useRSVPs } from '../hooks/useRSVPs';
import { AppShell } from '../components/layout/AppShell';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input, Textarea } from '../components/ui/Input';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { formatDisplayDate, formatTime, getNextSabbath } from '../lib/dates';
import { supabase } from '../lib/supabase';
import { Event, Profile } from '../types';

// ─── Avatar ──────────────────────────────────────────────────────────────────

function Avatar({
  profile,
  size = 'md',
}: {
  profile: Pick<Profile, 'full_name' | 'avatar_url'>;
  size?: 'sm' | 'md' | 'lg';
}) {
  const initials = profile.full_name
    .split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase();
  const sz = { sm: 'w-7 h-7 text-xs', md: 'w-9 h-9 text-sm', lg: 'w-12 h-12 text-base' }[size];

  if (profile.avatar_url) {
    return (
      <img
        src={profile.avatar_url}
        alt={profile.full_name}
        className={`${sz} rounded-full object-cover flex-shrink-0 border-2 border-white dark:border-gray-900`}
      />
    );
  }
  return (
    <div className={`${sz} rounded-full bg-warm-100 dark:bg-warm-900 flex items-center justify-center flex-shrink-0 border-2 border-white dark:border-gray-900`}>
      <span className="font-bold text-warm-600 dark:text-warm-300">{initials}</span>
    </div>
  );
}

// ─── InfoRow ─────────────────────────────────────────────────────────────────

function InfoRow({
  icon, iconBg = true, children,
}: {
  icon: React.ReactNode; iconBg?: boolean; children: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-2 text-sm">
      {iconBg ? (
        <div className="w-7 h-7 bg-warm-50 dark:bg-warm-950 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">{icon}</div>
      ) : (
        <div className="w-7 h-7 rounded-lg overflow-hidden flex-shrink-0 mt-0.5">{icon}</div>
      )}
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  );
}

// ─── NextSabbaths helper ─────────────────────────────────────────────────────

function getUpcomingSabbaths(count = 6): Date[] {
  const first = getNextSabbath();
  return Array.from({ length: count }, (_, i) => addWeeks(first, i));
}

// ─── Scheduled event card (next upcoming) ────────────────────────────────────

function UpcomingEventCard({
  event,
  onRefetch,
}: {
  event: Event;
  onRefetch: () => void;
}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { rsvps, upsertRSVP, myRSVP, comingCount } = useRSVPs(event.id);
  const [rsvping, setRsvping] = useState(false);

  const hostAddress = event.location_override || event.host?.address;
  const mapsUrl = hostAddress
    ? `https://maps.google.com/?q=${encodeURIComponent(hostAddress)}`
    : null;
  const myStatus = user ? myRSVP(user.id)?.status : null;
  const comingRSVPs = rsvps.filter((r) => r.status === 'coming');

  const handleClaimHost = async () => {
    if (!event || !user) return;
    await supabase.from('events').update({ host_user_id: user.id }).eq('id', event.id);
    onRefetch();
  };

  const handleQuickRSVP = async (status: 'coming' | 'not_coming') => {
    if (!user) return;
    setRsvping(true);
    await upsertRSVP(user.id, status);
    setRsvping(false);
  };

  return (
    <Card className="overflow-hidden" padding={false}>
      {/* Banner */}
      <div className="bg-gradient-to-br from-warm-500 to-warm-600 px-4 py-3">
        <span className="text-warm-100 text-xs font-medium uppercase tracking-wider">Upcoming</span>
        <h2 className="text-white text-lg font-bold mt-0.5">{event.title}</h2>
        <p className="text-warm-100 text-xs mt-0.5">{formatDisplayDate(event.date)}</p>
      </div>

      <div className="px-4 py-3 space-y-2">
        {event.time && (
          <InfoRow icon={<Clock size={15} className="text-warm-500" />}>
            <span className="text-gray-700 dark:text-gray-300">{formatTime(event.time)}</span>
          </InfoRow>
        )}

        {event.host ? (
          <InfoRow
            icon={
              event.host.avatar_url
                ? <img src={event.host.avatar_url} alt={event.host.full_name} className="w-8 h-8 rounded-xl object-cover" />
                : <HomeIcon size={15} className="text-warm-500" />
            }
            iconBg={!event.host.avatar_url}
          >
            <span className="text-gray-700 dark:text-gray-300">
              Hosted by{' '}
              <span className="font-medium text-gray-900 dark:text-gray-100">
                {event.host.full_name.split(' ')[0]}
                {event.host.spouse_name ? ` & ${event.host.spouse_name.split(' ')[0]}` : ''}
              </span>
            </span>
          </InfoRow>
        ) : (
          <div className="rounded-lg bg-amber-50 dark:bg-amber-950 border border-amber-100 dark:border-amber-900 px-3 py-2">
            <p className="text-amber-700 dark:text-amber-300 text-xs font-medium">This Sabbath needs a host.</p>
            <Button variant="primary" size="sm" className="mt-1.5 bg-amber-500 hover:bg-amber-600 h-8 text-xs" onClick={handleClaimHost}>
              I'll Host
            </Button>
          </div>
        )}

        {hostAddress && (
          <InfoRow icon={<MapPin size={15} className="text-warm-500" />}>
            {mapsUrl
              ? <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className="text-warm-600 dark:text-warm-400 underline underline-offset-2 leading-snug">{hostAddress}</a>
              : <span className="text-gray-600 dark:text-gray-300 leading-snug">{hostAddress}</span>
            }
          </InfoRow>
        )}

        <InfoRow icon={<Users size={15} className="text-warm-500" />}>
          <div className="flex-1">
            <span className="text-gray-700 dark:text-gray-300">
              <span className="font-semibold text-gray-900 dark:text-gray-100">{comingCount}</span>{' '}
              {comingCount === 1 ? 'person' : 'people'} coming
            </span>
            {comingRSVPs.length > 0 && (
              <div className="flex items-center gap-1 mt-1.5 flex-wrap">
                {comingRSVPs.slice(0, 6).map((r) =>
                  r.profile ? <Avatar key={r.id} profile={r.profile} size="sm" /> : null
                )}
                {comingRSVPs.length > 6 && (
                  <span className="text-xs text-gray-400 dark:text-gray-500 ml-1">+{comingRSVPs.length - 6} more</span>
                )}
              </div>
            )}
          </div>
        </InfoRow>

        {myStatus && (
          <div className={`rounded-xl px-3 py-2.5 flex items-center gap-2 text-sm font-medium ${
            myStatus === 'coming'
              ? 'bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-300'
              : 'bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400'
          }`}>
            {myStatus === 'coming'
              ? <><Check size={15} className="text-green-500 flex-shrink-0" /> You're coming! 🎉</>
              : <><X size={15} className="flex-shrink-0" /> You're not attending</>
            }
          </div>
        )}

        <div className="grid grid-cols-2 gap-1.5 mt-1">
          <button
            onClick={() => handleQuickRSVP('coming')}
            disabled={rsvping}
            className={`flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-semibold transition-all active:scale-[0.97] ${
              myStatus === 'coming'
                ? 'bg-green-500 text-white shadow-sm'
                : 'bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-300 hover:bg-green-100'
            }`}
          >
            <Check size={14} /> Coming
          </button>
          <button
            onClick={() => handleQuickRSVP('not_coming')}
            disabled={rsvping}
            className={`flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-semibold transition-all active:scale-[0.97] ${
              myStatus === 'not_coming'
                ? 'bg-gray-700 dark:bg-gray-600 text-white shadow-sm'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200'
            }`}
          >
            <X size={14} /> Can't Make
          </button>
        </div>

        <button
          onClick={() => navigate(`/events/${event.id}`)}
          className="w-full text-center text-xs text-gray-400 dark:text-gray-500 hover:text-warm-500 transition-colors py-0.5"
        >
          View details & sign up →
        </button>
      </div>
    </Card>
  );
}

// ─── Inline host form for an open Saturday ───────────────────────────────────

function HostThisDateForm({
  dateStr,
  onDone,
  onCancel,
}: {
  dateStr: string;
  onDone: () => void;
  onCancel: () => void;
}) {
  const { user } = useAuth();
  const [time, setTime] = useState('14:00');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    setSaving(true);
    setError('');
    const { error: err } = await supabase.from('events').insert({
      title: 'Sabbath Lunch',
      date: dateStr,
      time: time || null,
      type: 'sabbath_lunch',
      host_user_id: user.id,
      description: description || null,
      created_by: user.id,
    });
    setSaving(false);
    if (err) { setError(err.message); return; }
    onDone();
  };

  const displayDate = format(new Date(dateStr + 'T12:00:00'), 'MMMM d');

  return (
    <form onSubmit={handleSubmit} className="pt-3 border-t border-warm-100 dark:border-gray-800 space-y-3">
      <p className="text-sm text-gray-600 dark:text-gray-400">
        You'll be hosting on <span className="font-semibold text-gray-800 dark:text-gray-100">{displayDate}</span>.
      </p>
      <Input
        label="Start time"
        type="time"
        value={time}
        onChange={(e) => setTime(e.target.value)}
      />
      <Textarea
        label="Notes (optional)"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="Anything the group should know…"
      />
      {error && (
        <p className="text-xs text-red-500">{error}</p>
      )}
      <div className="flex gap-2">
        <Button variant="ghost" size="sm" type="button" onClick={onCancel}>Cancel</Button>
        <Button size="sm" loading={saving} type="submit">Confirm & Host</Button>
      </div>
    </form>
  );
}

// ─── Open Saturday row ────────────────────────────────────────────────────────

function OpenSabbathRow({
  date,
  onBooked,
}: {
  date: Date;
  onBooked: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const dateStr = format(date, 'yyyy-MM-dd');
  const label = format(date, 'EEEE, MMMM d');
  const showYear = date.getFullYear() !== new Date().getFullYear();

  return (
    <div className="bg-white dark:bg-gray-900 rounded-2xl border border-warm-100 dark:border-gray-800 px-4 py-3 shadow-sm">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between text-left"
      >
        <div>
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {label}
            {showYear && <span className="text-gray-400 dark:text-gray-500 font-normal"> {date.getFullYear()}</span>}
          </p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">No host yet — tap to claim it</p>
        </div>
        <div className={`w-7 h-7 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${
          expanded ? 'bg-warm-500 text-white' : 'bg-warm-50 dark:bg-gray-800 text-warm-500'
        }`}>
          <Plus size={15} className={`transition-transform ${expanded ? 'rotate-45' : ''}`} />
        </div>
      </button>

      {expanded && (
        <HostThisDateForm
          dateStr={dateStr}
          onDone={onBooked}
          onCancel={() => setExpanded(false)}
        />
      )}
    </div>
  );
}

// ─── Scheduled (future, non-next) event row ───────────────────────────────────

function ScheduledRow({ event }: { event: Event }) {
  const navigate = useNavigate();
  return (
    <button
      onClick={() => navigate(`/events/${event.id}`)}
      className="w-full bg-white dark:bg-gray-900 rounded-2xl border border-warm-100 dark:border-gray-800 px-4 py-3 shadow-sm text-left flex items-center justify-between hover:bg-warm-50 dark:hover:bg-gray-800 active:scale-[0.98] transition-all"
    >
      <div>
        <p className="text-sm font-medium text-gray-800 dark:text-gray-100">{event.title}</p>
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
          {format(new Date(event.date + 'T12:00:00'), 'MMMM d')}
          {event.host && ` · ${event.host.full_name.split(' ')[0]}'s`}
        </p>
      </div>
      <ChevronRight size={16} className="text-gray-300 dark:text-gray-600 flex-shrink-0" />
    </button>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function HomePage() {
  const { profile, isAdmin } = useAuth();
  const { events, loading: eventsLoading, refetch } = useEvents();
  const navigate = useNavigate();

  const [timedOut, setTimedOut] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setTimedOut(true), 6000);
    return () => clearTimeout(t);
  }, []);
  const loading = eventsLoading && !timedOut;

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const today = format(new Date(), 'yyyy-MM-dd');

  // All upcoming scheduled events (sorted ascending — useEvents already does this)
  const upcomingEvents = events.filter((e) => e.date >= today);

  // The very next one gets the full card treatment
  const nextEvent = upcomingEvents[0] ?? null;

  // The rest get compact rows
  const laterEvents = upcomingEvents.slice(1);

  // Scheduled dates set for deduplication in the Saturday list
  const scheduledDates = new Set(events.map((e) => e.date));

  // Next 8 Saturdays
  const sabbaths = getUpcomingSabbaths(8);

  // For each "later" event, see if it lands on a Saturday (already shown in ScheduledRow)
  // Interleave: show open Saturdays that fall before each later event, then the event, etc.
  // Build a unified "upcoming Saturdays" list merging scheduled and open dates chronologically
  type SabbathItem =
    | { kind: 'scheduled'; event: Event }
    | { kind: 'open'; date: Date };

  const allSabbathItems: SabbathItem[] = [];
  sabbaths.forEach((sat) => {
    const dateStr = format(sat, 'yyyy-MM-dd');
    const matchEvent = laterEvents.find((e) => e.date === dateStr);
    if (matchEvent) {
      allSabbathItems.push({ kind: 'scheduled', event: matchEvent });
    } else if (!scheduledDates.has(dateStr)) {
      allSabbathItems.push({ kind: 'open', date: sat });
    }
  });

  return (
    <AppShell>
      <div className="px-4 pt-12 space-y-4 pb-6">
        {/* Greeting */}
        <div className="flex items-center justify-between mb-2">
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">{greeting()},</p>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {profile?.full_name?.split(' ')[0] ?? 'Friend'} 👋
            </h1>
          </div>
          {profile && (
            <button onClick={() => navigate('/settings')} className="active:scale-95 transition-transform">
              <Avatar profile={profile} size="lg" />
            </button>
          )}
        </div>

        {loading ? (
          <LoadingSpinner />
        ) : (
          <>
            {/* Next upcoming event — full card */}
            {nextEvent && (
              <UpcomingEventCard event={nextEvent} onRefetch={refetch} />
            )}

            {/* Upcoming Saturdays section */}
            <div>
              <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-widest px-1 mb-3">
                {nextEvent ? 'Coming Up' : 'Upcoming Saturdays'}
              </p>

              {allSabbathItems.length === 0 && !nextEvent ? (
                <Card className="text-center py-10">
                  <div className="text-4xl mb-3">🕊️</div>
                  <h2 className="font-semibold text-gray-700 dark:text-gray-300">No upcoming lunches</h2>
                  <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                    Tap a Saturday below to host one.
                  </p>
                </Card>
              ) : (
                <div className="space-y-2">
                  {allSabbathItems.map((item) =>
                    item.kind === 'scheduled' ? (
                      <ScheduledRow key={item.event.id} event={item.event} />
                    ) : (
                      <OpenSabbathRow
                        key={format(item.date, 'yyyy-MM-dd')}
                        date={item.date}
                        onBooked={refetch}
                      />
                    )
                  )}
                </div>
              )}
            </div>

            {/* Custom date — for non-Saturday events (admin only) */}
            {isAdmin && (
              <button
                onClick={() => navigate('/admin/create-event')}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl border border-dashed border-gray-200 dark:border-gray-700 text-sm text-gray-400 dark:text-gray-500 hover:border-warm-300 hover:text-warm-500 dark:hover:border-warm-700 dark:hover:text-warm-400 transition-colors"
              >
                <Plus size={15} />
                Schedule on a custom date
              </button>
            )}
          </>
        )}
      </div>
    </AppShell>
  );
}
